import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "./LoadingSpinner";
import friendService from "../services/friendService";
import { SearchIcon } from "./hand/icons";
import { CloseFriendMark, MirrorRoom, useCloseFriend } from "./closefriends";
import PortalRing from "./stories/PortalRing";
import { SuggestionCard } from "./PeopleYouMayKnow";
import {
  RingButton,
  SectionHeading,
  usePeopleToast,
  errorMessage,
} from "./people/PeopleUI";
import { storedLocation } from "../data/routeData";
import { tintFor } from "../utils/tints";

export const livePortalCount = (person) =>
  person.livePortalCount ??
  (new Date(person.activeSignalExpiresAt).getTime() > Date.now() ? 1 : 0);
export function FriendCard({
  person,
  onOpen,
  onMessage,
  onPortal,
  onViewPortal,
}) {
  const close = useCloseFriend(
    person.userId,
    person.isClose,
    person.displayName
  );
  const live = livePortalCount(person);
  const upcoming = person.upcomingEventCount || 0;
  const status =
    close.error ||
    (close.flash
      ? "Close friend · They'll know"
      : live
      ? live + " portal" + (live === 1 ? "" : "s") + " live"
      : upcoming
      ? upcoming + " event" + (upcoming === 1 ? "" : "s") + " coming up"
      : "No plans yet");
  const tone = close.flash
    ? "is-flash"
    : close.error
    ? ""
    : live
    ? "is-live"
    : upcoming
    ? "is-upcoming"
    : "";
  return (
    <article className="people-friend">
      <div className="people-friend-top">
        <PortalRing
          size={64}
          state={live ? "live" : "seen"}
          avatarUrl={person.avatarUrl}
          fallbackTint={tintFor(person.userId || person.displayName)}
          initial={(person.displayName || "?")[0]}
          onClick={live && onViewPortal ? onViewPortal : onOpen}
          ariaLabel={"View " + person.displayName}
        />
        <button type="button" onClick={onOpen} className="people-friend-info">
          <span className="people-friend-name">{person.displayName}</span>
          <span className="people-meta">
            {[person.handle && "@" + person.handle, person.location]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </button>
      </div>
      <CloseFriendMark
        size="corner"
        on={close.on}
        q={close.q}
        onToggle={close.toggle}
        name={person.displayName}
      />
      <div
        role={close.error ? "alert" : undefined}
        className={"people-friend-status " + tone}
      >
        {live && !close.flash && !close.error ? (
          <i className="people-dot" />
        ) : null}
        {status}
      </div>
      <div className="people-friend-actions">
        <button type="button" className="people-pill is-grey" onClick={onOpen}>
          Portal Page
        </button>
        <button type="button" className="people-pill" onClick={onMessage}>
          Message
        </button>
        <RingButton dark onClick={onPortal} label="Open a portal" />
      </div>
      <MirrorRoom q={close.q} surface="card" first={close.first} />
    </article>
  );
}

export default function FriendsDirectory({
  initialFriends,
  onCount,
  onViewPortal,
}) {
  const navigate = useNavigate();
  const [friends, setFriends] = useState(initialFriends || []);
  const [loading, setLoading] = useState(!initialFriends);
  const [error, setError] = useState(null);
  const [retry, setRetry] = useState(0);
  const [sort, setSort] = useState("az");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [sentIds, setSentIds] = useState(new Set());
  const [busy, setBusy] = useState(new Set());
  const pending = useRef(new Set());
  const [notify, toast] = usePeopleToast();
  useEffect(() => {
    if (initialFriends && !retry) {
      setFriends(initialFriends);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    friendService
      .getFriends()
      .then((result) => {
        if (cancelled) return;
        if (result.success) {
          setFriends(result.data || []);
          setError(null);
        } else setError(errorMessage(result, "Could not load friends."));
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setError("Could not load friends.");
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [initialFriends, retry]);
  useEffect(() => {
    if (!loading && !error) onCount?.(friends.length);
  }, [friends.length, loading, error, onCount]);
  useEffect(() => {
    let cancelled = false;
    const q = query.trim();
    setResults([]);
    setSearchError(null);
    setSearching(!!q);
    if (!q) return;
    const timer = setTimeout(async () => {
      try {
        const result = await friendService.searchPeople(q, 20);
        if (cancelled) return;
        if (result.success) setResults(result.data || []);
        else setSearchError(errorMessage(result, "Could not search people."));
      } catch {
        if (!cancelled) setSearchError("Could not search people.");
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);
  const open = useCallback(
    (p) => navigate(p.handle ? "/" + p.handle : "/users/" + p.userId),
    [navigate]
  );
  const add = async (person) => {
    if (person.relationshipStatus === "request_received") {
      open(person);
      return;
    }
    if (pending.current.has(person.userId) || sentIds.has(person.userId))
      return;
    pending.current.add(person.userId);
    setBusy(new Set(pending.current));
    try {
      const result = await friendService.sendFriendRequest(person.userId);
      if (!result.success)
        throw new Error(errorMessage(result, "Could not send request."));
      setSentIds((prev) => new Set(prev).add(person.userId));
      notify("Request sent to " + person.displayName);
    } catch (e) {
      notify(e.message);
    } finally {
      pending.current.delete(person.userId);
      setBusy(new Set(pending.current));
    }
  };
  const q = query.trim().toLowerCase();
  const city = (storedLocation().label || "").split(",")[0].toLowerCase();
  const matches = useMemo(() => {
    const rows = friends.filter(
      (p) =>
        !q ||
        [p.displayName, p.handle, p.location].some((value) =>
          (value || "").toLowerCase().includes(q)
        )
    );
    const az = (a, b) =>
      (a.displayName || "").localeCompare(b.displayName || "");
    return rows.sort((a, b) => {
      if (sort === "active")
        return (
          livePortalCount(b) - livePortalCount(a) ||
          (b.upcomingEventCount || 0) - (a.upcomingEventCount || 0) ||
          az(a, b)
        );
      if (sort === "nearby") {
        const local = (p) =>
          !!city && (p.location || "").toLowerCase().includes(city);
        return (
          Number(local(b)) - Number(local(a)) ||
          (a.location || "zzz").localeCompare(b.location || "zzz") ||
          az(a, b)
        );
      }
      return az(a, b);
    });
  }, [friends, q, sort, city]);
  const ids = new Set(friends.map((p) => p.userId));
  const others = results.filter(
    (p) => !ids.has(p.userId) && p.relationshipStatus !== "friends"
  );
  return (
    <div>
      <div className="people-friend-tools">
        <label className="people-search">
          <SearchIcon size={18} />
          <input
            aria-label="Search friends or find new people"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search friends or find new people…"
          />
        </label>
        <div className="people-sort" aria-label="Sort friends">
          {[
            ["az", "A–Z"],
            ["nearby", "Nearby"],
            ["active", "Active"],
          ].map(([key, label]) => (
            <button
              type="button"
              key={key}
              aria-pressed={sort === key}
              onClick={() => setSort(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <SectionHeading
        detail={
          loading
            ? "…"
            : q
            ? matches.length + " of " + friends.length
            : String(friends.length)
        }
      >
        Your friends
      </SectionHeading>
      {error && (
        <p role="alert" className="people-error">
          {error}{" "}
          <button type="button" onClick={() => setRetry((v) => v + 1)}>
            Retry
          </button>
        </p>
      )}
      {loading ? (
        <LoadingSpinner label="Loading friends…" />
      ) : !matches.length ? (
        <p className="people-empty">
          {q
            ? "No friends match that."
            : "No friends yet — search to add some."}
        </p>
      ) : (
        <div className="people-friends">
          {matches.map((person) => (
            <FriendCard
              key={person.userId}
              person={person}
              onOpen={() => open(person)}
              onMessage={() => navigate("/messages?with=" + person.userId)}
              onPortal={() => navigate("/portals/new")}
              onViewPortal={
                onViewPortal
                  ? (event) => onViewPortal(person.userId, event)
                  : undefined
              }
            />
          ))}
        </div>
      )}
      {q && (
        <section style={{ marginTop: 32 }}>
          <SectionHeading detail={searching ? "…" : String(others.length)}>
            More people
          </SectionHeading>
          {searchError && (
            <p role="alert" className="people-error">
              {searchError}
            </p>
          )}
          {searching ? (
            <LoadingSpinner label="Searching people…" />
          ) : !others.length ? (
            <p className="people-empty">
              No other people for “{query.trim()}”.
            </p>
          ) : (
            <div className="people-suggestions">
              {others.map((person) => (
                <SuggestionCard
                  key={person.userId}
                  person={{
                    ...person,
                    requestSent:
                      sentIds.has(person.userId) ||
                      person.relationshipStatus === "request_sent",
                  }}
                  busy={busy.has(person.userId)}
                  onOpen={() => open(person)}
                  onAdd={() => add(person)}
                />
              ))}
            </div>
          )}
        </section>
      )}
      {toast}
    </div>
  );
}
